import { useRef, useState, useEffect, useCallback } from "react";
import { Eraser, CheckCircle, PenNib } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

export const SignatureDialog = ({ order, onClose, onSaved }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState(null);
  const [recollect, setRecollect] = useState(false);

  useEffect(() => {
    if (!order) return;
    setExisting(null);
    setRecollect(false);
    if (order.has_signature) {
      api.get(`/orders/${order.id}/signature`).then((r) => setExisting(r.data)).catch(() => {});
    }
  }, [order]);

  const setupCanvas = useCallback((node) => {
    canvasRef.current = node;
    if (!node) return;
    const dpr = window.devicePixelRatio || 1;
    const w = node.offsetWidth;
    node.width = w * dpr;
    node.height = 220 * dpr;
    const ctx = node.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, 220);
    ctx.strokeStyle = "#1e293b";
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    hasInk.current = false;
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const down = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    hasInk.current = true;
  };
  const up = () => { drawing.current = false; };

  const clear = () => setupCanvas(canvasRef.current);

  const save = async () => {
    if (!hasInk.current) { toast.error("O cliente precisa assinar antes de salvar"); return; }
    setSaving(true);
    try {
      const image = canvasRef.current.toDataURL("image/png");
      await api.post(`/orders/${order.id}/signature`, { image });
      toast.success("Assinatura registrada com sucesso");
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    }
    setSaving(false);
  };

  const showPad = !existing || recollect;

  return (
    <Dialog open={!!order} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <PenNib size={18} className="text-indigo-400" /> Assinatura — Pedido #{order?.code}
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            {showPad ? "Peça para o cliente assinar no quadro abaixo (dedo ou caneta)" : "Assinatura registrada para este pedido"}
          </DialogDescription>
        </DialogHeader>
        {order?.has_signature && !existing && !recollect && (
          <p className="text-sm text-zinc-500 py-4 text-center">Carregando assinatura...</p>
        )}
        {existing && !recollect && (
          <div className="space-y-3" data-testid="signature-view">
            <div className="bg-white rounded-lg p-2">
              <img src={existing.image} alt="Assinatura do cliente" className="w-full" data-testid="signature-image" />
            </div>
            <p className="text-xs text-zinc-400" data-testid="signature-meta">
              Assinado em <span className="text-zinc-200">{fmtDate(existing.signed_at?.slice(0, 10))}</span>
              {" às "}{existing.signed_at?.slice(11, 16)} (UTC) — coletado por{" "}
              <span className="text-zinc-200">{existing.collected_by_name}</span>
            </p>
            {isAdmin && (
              <button data-testid="recollect-signature-button" onClick={() => setRecollect(true)}
                className="w-full text-sm text-amber-400 border border-amber-400/30 hover:bg-amber-400/10 py-2.5 rounded-lg transition-colors">
                Substituir assinatura (admin)
              </button>
            )}
          </div>
        )}
        {showPad && (
          <div className="space-y-3">
            <canvas
              ref={setupCanvas}
              data-testid="signature-canvas"
              className="w-full rounded-lg border-2 border-dashed border-zinc-600 touch-none cursor-crosshair"
              style={{ height: 220, background: "#fff" }}
              onPointerDown={down}
              onPointerMove={move}
              onPointerUp={up}
              onPointerLeave={up}
            />
            <div className="flex gap-2">
              <button data-testid="signature-clear-button" onClick={clear} type="button"
                className="flex-1 flex items-center justify-center gap-2 text-sm text-zinc-300 border border-zinc-700 hover:bg-zinc-800 py-3 min-h-[44px] rounded-lg transition-colors">
                <Eraser size={16} /> Limpar
              </button>
              <button data-testid="signature-save-button" onClick={save} disabled={saving} type="button"
                className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-medium py-3 min-h-[44px] rounded-lg transition-colors">
                <CheckCircle size={16} weight="bold" /> {saving ? "Salvando..." : "Confirmar Recebimento"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
